import React, { useMemo } from 'react';

import {
	GlobalStyle,
	AppContainer,
	Settings,
	GameRules,
	Rules,
	ProvablyFair,
	History,
	SetStake,
	AutoForm,
	Error,
	Notification,
	Main,
	Header,
	Loading,
	BetReply,
	StopFooter
} from "@orbital/games-frontend-library"
import Footer from './components/footer/Footer';
import CustomReply from './components/betReply/CustomReply';
import RisksAndRows from './components/risksAndRows/RisksAndRows';
import InfoBox from './components/infoBox/InfoBox';
import Audio from './assets/audio/audio';
import {
	settings,
	gamePlayCustomatization,
	gamePlayCustomatizationOptions,
	autoPlay
} from './lib/CONTENTS';
import { colors } from './lib/colors';
import BezierScene from './gamePlay/BezierScene/BezierScene';

import { PROPS } from './inteface';

const MAX_WIN = 1000;

export default function Plinko({
	stake,
	handleStake,
	balanceUpdateOn,
	finalBalance,
	gameInfo,
	handleAudio,
	handleAbove,
	handleAutoConfigs,
	seeds,
	betReply,
	history,
	blur,
	unBlur,
	settingsUnblur,
	above,
	isLoaded,
	errorText,
	notification,
	notificationfadeOut,
	speed,
	toggleSpeed,
	isFinished,
	handleFinish,
	path,
	ballBalance,
	winBalance,
	activeRows,
	activeRisk,
	coeffs,
	wheelCoefs,
	ballsOnBoard,
	handleNotification,
	isAutoOn,
	stopAuto,
	betHandler,
	betCount,
	numberOfBets,
	selectRisk,
	selectRows,
	seedRequest,
	newSeedRequest,
	precision,
	user_id,
	requestBetReply,
	roundId,
	setRoundId,
	autoConfigs,
	enableLoading,
	duration,
	SFXS,
	audio,
	betSent
}: PROPS) {
	const HeaderColors = {
		...colors,
		quaternary: colors.primaryDark,
		tertiary: colors.primaryDark
	};
	const verifierFunc = () => {
		window.open('https://codepen.io/orbitalgaming/pen/oNVLePv', '_blank');
	};

	const maxCoeff = useMemo(() => {
		if (gameInfo.coeffs && gameInfo.coeffs[+activeRows - 8]) {
			const currentCoeffs = gameInfo.coeffs[+activeRows - 8][activeRisk.toLowerCase()];
			if (currentCoeffs && Array.isArray(currentCoeffs) && currentCoeffs.length > 0) {
				return Math.max(...currentCoeffs.map((c: string) => parseFloat(c) || 0));
			}
		}
		return 0;
	}, [gameInfo.coeffs, activeRows, activeRisk]);

	return (
		<AppContainer color={colors.primary}>
			<Audio
				audio={audio}
				errorText={errorText}
				isLoaded={isLoaded}
				speed={speed}
			/>
			<GlobalStyle />
			<Main blur={blur}>
				<Header
					title='Plinko'
					letterSpace='1px'
					betInfoBalance={finalBalance || 0}
					gameInfo={gameInfo}
					handleAbove={handleAbove}
					colors={HeaderColors}
					// audio={audio}
					sfx={SFXS.goForwardSFX}
					balanceUpdateOn={balanceUpdateOn}
					updateNow={false}
					fontSize={'20px'}
				/>

				<BezierScene
					path={path}
					ballBalance={ballBalance}
					winBalance={winBalance}
					rows={+activeRows}
					risk={activeRisk}
					coeffs={coeffs}
					handleFinish={handleFinish}
					speed={speed}
					wheelCoefs={wheelCoefs}
				/>

				<InfoBox
					risk={activeRisk}
					rows={activeRows}
					isClickable={!ballsOnBoard}
					handleAbove={handleAbove}
					handleNotification={handleNotification}
					audio={audio}
				/>

				{!isAutoOn ? (
					<Footer
						stake={stake}
						handleStake={handleStake}
						speed={speed}
						sendSettings={toggleSpeed}
						sendBet={betHandler}
						handleAbove={handleAbove}
						handleNotification={handleNotification}
						gameInfo={gameInfo}
						audio={audio}
						colors={colors}
						betSent={betSent}
					/>
				) : (
					<StopFooter
						toggleIsAutoOn={stopAuto}
						bet={stake}
						toFix={gameInfo.precision}
						betCount={+betCount}
						betTotal={numberOfBets}
						fadeOut={false}
						currency={gameInfo.currency}
					/>
				)}
			</Main>

			{above === 'settings' && (
				<Settings
					clickable={true}
					unBlur={settingsUnblur}
					colors={{ ...colors, tertiary: colors.primary }}
					handleAbove={handleAbove}
					handleAudio={handleAudio}
					audio={audio}
				/>
			)}
			{above === 'Risks and Rows' && (
				<RisksAndRows
					colors={colors}
					activeRisk={activeRisk}
					selectRisk={selectRisk}
					activeRows={activeRows}
					selectRows={selectRows}
					handleAbove={handleAbove}
					audio={audio}
				/>
			)}
			{above === 'Game Rules' && (
				<GameRules colors={colors} handleAbove={handleAbove} audio={audio}>
					<h3>Settings</h3>
					<Rules content={settings} />
					{maxCoeff > 0 && gameInfo.maxBet && (
						<p>The max win is {`${gameInfo.currency || ''} ${gameInfo.maxBet * MAX_WIN}`} of your bet</p>
					)}
					{gameInfo.maxBet && gameInfo.minBet && (
						<p>
							Maximum Bet: {`${gameInfo.currency || ''} ${gameInfo.maxBet}`} Minimum Bet:{' '}
							{`${gameInfo.currency || ''} ${gameInfo.minBet}`}
						</p>
					)}
					<h3>Gameplay Customization</h3>
					<Rules content={gamePlayCustomatization} />
					<h5>Options:</h5>
					<Rules content={gamePlayCustomatizationOptions} />
					<h3>Auto play Options:</h3>
					<Rules content={autoPlay} />
				</GameRules>
			)}
			{above === 'Provably Fair' && (
				<ProvablyFair
					colors={{ ...colors, tertiary: colors.secondaryBackground }}
					handleAbove={handleAbove}
					seeds={seeds}
					seedRequest={seedRequest}
					newSeedRequest={newSeedRequest}
					handleNotification={handleNotification}
					audio={audio}
					roundIsOn={isAutoOn}
					verifierFunc={verifierFunc}
				/>
			)}
			{above === 'History' && (
				<History
					clickable={true}
					unBlur={unBlur}
					handleAbove={handleAbove}
					history={history}
					audio={audio}
					toFix={precision}
					user_id={user_id}
					requestBetReply={requestBetReply}
					colors={colors}
					handleNotification={handleNotification}
					setRoundId={setRoundId}
					payout={true}
				/>
			)}
			{above === 'Bet Reply' && (
				<BetReply
					betReplyResponse={betReply}
					handleNotification={handleNotification}
					roundId={roundId}
					audio={audio}
					colors={colors}
					handleAbove={handleAbove}
					toFix={precision}
					headerpadding={'0 30px'}
				>
					<CustomReply betReplyResponse={betReply} />
				</BetReply>
			)}
			{above === 'SetStake' && (
				<SetStake
					currency={gameInfo.currency}
					stake={stake}
					gameInfo={gameInfo}
					handleStake={handleStake}
					colors={{ ...colors, tertiaryDark: colors.tertiary }}
					audio={audio}
					handleAbove={handleAbove}
					handleNotification={handleNotification}
					size={'20px'}
				/>
			)}
			{above === 'Auto' && (
				<AutoForm
					stake={stake}
					handleStake={handleStake}
					autoConfigs={autoConfigs}
					handleAutoConfigs={handleAutoConfigs}
					colors={{ ...colors, tertiaryDark: colors.tertiary }}
					handleAbove={handleAbove}
					audio={audio}
					handleNotification={handleNotification}
					currency={gameInfo.currency}
					gameInfo={{ ...gameInfo, toFix: gameInfo.precision }}
					SFXS={SFXS}
				/>
			)}

			{!isLoaded && enableLoading && <Loading duration={duration} />}
			{!!notification && (
				<Notification
					colors={{ ...colors, tertiary: colors.primary }}
					fadeOut={notificationfadeOut}
					handleNotification={handleNotification}
					text={notification}
					unBlur={unBlur}
					audio={audio}
					// playAudio={audioNotification}
				/>
			)}
			{!!errorText && (
				<Error
					text={errorText}
					unBlur={false}
					// playAudio={audioError}
					audio={audio}
				/>
			)}
		</AppContainer>
	);
}
